import { API_URLS } from '../urls';

/**
 * « Monter sur le plateau » (2026-10-15) : les spectateurs connectés de Kleon.tv
 * demandent à devenir caméra invitée d'un direct, le réalisateur accepte ou refuse.
 * admin-api, StageRequestController.
 *
 * Relevé périodique, comme la modération du chat : la demande vient du portail,
 * qui n'a pas de canal temps réel vers la régie. `no-store` pour que le relevé
 * voie vraiment le serveur.
 *
 * Accepter crée la caméra de l'invité sur le microservice de la chaîne, en mode
 * lien, et réserve sa place sur le quota d'appareils (409 `full` quand il n'y en
 * a plus). L'invité arrive dans les sources distantes, en coulisses.
 */

export interface StagePendingRequest {
  id          : number;
  displayName : string;
  message     : string;
  createdAt   : string;
  expiresAt   : string;
  /** Salle d'attente : 0 tant que le spectateur n'a pas ouvert sa caméra ; sinon
   *  le numéro de son offre WebRTC, qui monte à chaque reconnexion. */
  previewVersion? : number;
}

export interface StageGuest {
  id          : number;
  displayName : string;
  message     : string;
  since       : string | null;
  /** Identifiant de la caméra sur le microservice : le relie à sa source. */
  deviceId    : string | null;
}

export interface StageState {
  channel    : { id: number; active: boolean; createdFromRegie: boolean; stageOpen: boolean };
  pending    : StagePendingRequest[];
  onStage    : StageGuest[];
  serverTime : string;
}

export class StageRequestError extends Error {
  constructor(message: string, public readonly status: number, public readonly code?: string) {
    super(message);
  }
}

const readJson = async <T>(res: Response): Promise<T> => {
  const data = await res.json().catch(() => ({}));

  if (!res.ok) throw new StageRequestError(data.error ?? `HTTP ${res.status}`, res.status, data.code);

  return data as T;
};

const postJson = <T>(url: string, body: object): Promise<T> =>
  fetch(url, {
    method  : 'POST',
    headers : { 'Content-Type': 'application/json' },
    body    : JSON.stringify(body),
  }).then(res => readJson<T>(res));

export const fetchStageState = async (adminId: number, channelId: number): Promise<StageState> => {
  const res  = await fetch(`${API_URLS.getStageRequests}?adminId=${adminId}&channelId=${channelId}`, { cache: 'no-store' });
  const data = await readJson<StageState>(res);

  return {
    ...data,
    pending : Array.isArray(data.pending) ? data.pending : [],
    onStage : Array.isArray(data.onStage) ? data.onStage : [],
  };
};

/** « Ouvrir le plateau au public » : même réglage que l'onglet Configuration. */
export const setStageOpen = (adminId: number, channelId: number, open: boolean) =>
  postJson<{ ok: boolean; stageOpen: boolean }>(API_URLS.updateStageChannel, { adminId, channelId, open });

const actionUrl = (id: number, action: 'accept' | 'refuse' | 'remove' | 'preview') =>
  API_URLS.stageRequestAction.replace('{id}', String(id)).replace('{action}', action);

export const acceptStageRequest = (adminId: number, id: number) =>
  postJson<{ ok: boolean; deviceId: string; label: string; nbDevicesUsed: number }>(actionUrl(id, 'accept'), { adminId });

export const refuseStageRequest = (adminId: number, id: number) =>
  postJson<{ ok: boolean }>(actionUrl(id, 'refuse'), { adminId });

/** Retire l'invité du plateau : sa caméra est supprimée, sa place rendue. */
export const removeStageGuest = (adminId: number, id: number) =>
  postJson<{ ok: boolean }>(actionUrl(id, 'remove'), { adminId });

// ── Salle d'attente (2026-10-16) ─────────────────────────────────────────────
// Le spectateur en attente offre une liaison WebRTC (sa caméra, son micro) ;
// la régie y répond, voit sa caméra et lui parle avant d'accepter ou de refuser.
// Les deux SDP transitent par la demande (admin-api ↔ customer-api).

/** L'offre du spectateur, avec son numéro de version. */
export const fetchStagePreviewOffer = async (adminId: number, id: number): Promise<{ offer: string; version: number }> => {
  const res = await fetch(`${actionUrl(id, 'preview')}?adminId=${adminId}`, { cache: 'no-store' });
  return readJson(res);
};

/** La réponse de la régie ; 409 `stale` quand le spectateur a refait une offre entre-temps. */
export const sendStagePreviewAnswer = (adminId: number, id: number, version: number, answer: string) =>
  postJson<{ ok: boolean }>(actionUrl(id, 'preview'), { adminId, version, answer });
